import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ArrowLeft, XIcon } from 'lucide-react';
import { type ComponentProps, useRef } from 'react';

import { cn } from '../lib/cn';

/**
 * Diálogos y paneles laterales sobre Radix Dialog: foco atrapado dentro, Escape cierra, clic en el
 * fondo cierra y, al cerrar, el foco vuelve al botón que lo abrió. Todos llevan una cruz visible
 * ("Cerrar") arriba a la derecha; los paneles de detalle, que en el móvil ocupan toda la pantalla,
 * además una flecha "Volver" arriba a la izquierda (el gesto que se espera en una pantalla completa).
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export function DialogOverlay({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn('fixed inset-0 z-50 bg-foreground/40 backdrop-blur-[2px]', className)}
      {...props}
    />
  );
}

/** Cruz de cierre: 36 px de área táctil, con nombre accesible "Cerrar". */
export function CloseButton({
  className,
  label = 'Cerrar',
  ...props
}: ComponentProps<typeof DialogPrimitive.Close> & { label?: string }) {
  return (
    <DialogPrimitive.Close
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      {...props}
    >
      <XIcon className="size-[1.1rem]" aria-hidden />
    </DialogPrimitive.Close>
  );
}

type ContentProps = ComponentProps<typeof DialogPrimitive.Content>;

/**
 * Radix devuelve el foco al `DialogTrigger`; la mayoría de los paneles de la consola se abren con
 * estado (una fila, "Ver conversación"), sin trigger, y el foco caía al <body>. Se guarda el
 * elemento que tenía el foco al abrir y se le devuelve al cerrar (si sigue en la página).
 */
function useReturnFocus({
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
}: {
  onOpenAutoFocus?: ((e: Event) => void) | undefined;
  onCloseAutoFocus?: ((e: Event) => void) | undefined;
  onEscapeKeyDown?: ((e: KeyboardEvent) => void) | undefined;
}) {
  const opener = useRef<HTMLElement | null>(null);
  const self = useRef<HTMLDivElement | null>(null);
  return {
    ref: self,
    // Con un diálogo abierto encima de otro (el visor del email sobre el panel de la
    // conversación), Escape cierra SÓLO el de arriba: si al abrir el de arriba este todavía
    // escuchaba el teclado, se cerraban los dos.
    onEscapeKeyDown: (e: KeyboardEvent) => {
      onEscapeKeyDown?.(e);
      const open = document.querySelectorAll('[role="dialog"][data-state="open"]');
      const top = open[open.length - 1];
      if (top && self.current && top !== self.current) e.preventDefault();
    },
    onOpenAutoFocus: (e: Event) => {
      opener.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      onOpenAutoFocus?.(e);
      if (e.defaultPrevented || !(e.target instanceof HTMLElement)) return;
      // Radix enfoca el primer elemento enfocable. Si es un iframe (un email en el hilo), el foco
      // queda DENTRO del iframe y las teclas —Escape incluida— no llegan a la página: se enfoca la
      // cruz visible en su lugar.
      const first = [
        ...e.target.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])',
        ),
      ].find((el) => el.offsetParent !== null || el.getClientRects().length > 0);
      if (first?.tagName === 'IFRAME') {
        const close = [
          ...e.target.querySelectorAll<HTMLElement>('button[aria-label="Cerrar"]'),
        ].find((el) => el.getClientRects().length > 0);
        e.preventDefault();
        (close ?? e.target).focus();
      }
    },
    onCloseAutoFocus: (e: Event) => {
      onCloseAutoFocus?.(e);
      if (e.defaultPrevented) return;
      const el = opener.current;
      if (el?.isConnected && el !== document.body) {
        e.preventDefault();
        el.focus();
      }
    },
  };
}

export function DialogContent({
  className,
  children,
  showClose = true,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  ...props
}: ContentProps & { showClose?: boolean }) {
  const focus = useReturnFocus({ onOpenAutoFocus, onCloseAutoFocus, onEscapeKeyDown });
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          'fixed top-1/2 left-1/2 z-50 grid max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-xl border bg-popover p-6 text-popover-foreground shadow-popover outline-none',
          className,
        )}
        {...props}
        {...focus}
      >
        {children}
        {showClose ? <CloseButton className="absolute top-3 right-3" /> : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

/**
 * Panel lateral (drawer). `side="right"` es un panel de detalle: en el móvil ocupa la pantalla y
 * lleva una barra con "Volver" y la cruz; desde `sm` la cruz queda flotando arriba a la derecha
 * (el contenido reserva ese hueco con `pr-10` en su cabecera). `side="left"` es el menú.
 */
export function SheetContent({
  className,
  children,
  side = 'left',
  closeClassName,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  ...props
}: ContentProps & {
  side?: 'left' | 'right';
  closeClassName?: string;
}) {
  const focus = useReturnFocus({ onOpenAutoFocus, onCloseAutoFocus, onEscapeKeyDown });
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          'fixed inset-y-0 z-50 flex w-72 max-w-[85vw] flex-col shadow-xl outline-none',
          side === 'left' ? 'left-0' : 'right-0',
          className,
        )}
        {...props}
        {...focus}
      >
        {side === 'right' ? (
          <div className="flex h-14 shrink-0 items-center justify-between border-b bg-background/95 px-2 backdrop-blur sm:hidden">
            <DialogPrimitive.Close
              aria-label="Volver"
              className="inline-flex h-10 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ArrowLeft className="size-[1.1rem]" aria-hidden />
              Volver
            </DialogPrimitive.Close>
            <CloseButton className={closeClassName} />
          </div>
        ) : null}
        {children}
        <CloseButton
          className={cn(
            'absolute top-3 right-3 z-10',
            side === 'right' && 'hidden sm:inline-flex',
            closeClassName,
          )}
        />
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-1.5 pr-8', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('text-lg font-semibold tracking-tight', className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}
